import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime, formatMoney } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { givingApi } from '@/services/giving.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner, StatusBadge } from '@/components/ui';
import { RecordDonationDialog } from './record-donation-dialog';

/** Donations, most recent first, with an inline refund — a correction is a state change, not an erasure. */
export function GivingListPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [isRecording, setIsRecording] = useState(false);

  const query = useQuery({
    queryKey: ['giving', 'donations'],
    queryFn: ({ signal }) => givingApi.list({ limit: 50 }, signal),
    placeholderData: (previous) => previous,
  });

  const refund = useMutation({
    mutationFn: (id: string) => givingApi.refund(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['giving', 'donations'], exact: false });
    },
  });

  const donations = query.data?.data ?? [];
  const isStale = query.isPlaceholderData;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('giving.donation.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('giving.donation.count', { count: donations.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>

        {can('giving.donation.create') && (
          <Button variant="primary" onClick={() => setIsRecording(true)}>
            {t('giving.donation.record')}
          </Button>
        )}
      </header>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}
      {refund.isError && (
        <Alert tone="danger">
          {refund.error instanceof ApiError
            ? t(`errors.${refund.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && donations.length === 0 && (
        <EmptyState title={t('giving.donation.empty')} hint={t('giving.donation.record')} />
      )}

      {donations.length > 0 && (
        <div className="table-scroll" data-stale={isStale || undefined}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('giving.donation.donor')}</th>
                <th scope="col">{t('giving.donation.fund')}</th>
                <th scope="col">{t('giving.donation.amount')}</th>
                <th scope="col">{t('giving.donation.method')}</th>
                <th scope="col">{t('common.labels.status')}</th>
                <th scope="col">{t('giving.donation.receivedAt')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {donations.map((donation) => (
                <tr key={donation.id}>
                  <td>
                    {donation.donorPersonId ? (
                      <Link to={`/people/${donation.donorPersonId}`} className="person-name">
                        {donation.donorDisplayName}
                      </Link>
                    ) : (
                      <span className="cell-empty">{t('giving.donation.anonymous')}</span>
                    )}
                  </td>
                  <td>{t(`giving.fund.${donation.fund}`)}</td>
                  <td dir="ltr">{formatMoney(donation.amount, donation.currency, locale)}</td>
                  <td>{t(`giving.method.${donation.method}`)}</td>
                  <td>
                    <StatusBadge
                      status={donation.status}
                      label={t(`giving.status.${donation.status}`)}
                    />
                  </td>
                  <td dir="ltr">{formatDateTime(donation.receivedAt, locale)}</td>
                  <td>
                    {can('giving.donation.refund') && donation.status !== 'REFUNDED' && (
                      <Button
                        variant="ghost"
                        loading={refund.isPending && refund.variables === donation.id}
                        onClick={() => refund.mutate(donation.id)}
                      >
                        {t('giving.donation.refund')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isRecording && (
        <RecordDonationDialog
          onClose={() => setIsRecording(false)}
          onRecorded={() => {
            setIsRecording(false);
            void queryClient.invalidateQueries({ queryKey: ['giving', 'donations'], exact: false });
          }}
        />
      )}
    </div>
  );
}
